import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type FreeRent = {
  freerentid: number
}
// private module logic //

const id: string = "freerent"
const initialState: ApiResult<FreeRent[]> = { id, state: "init", value: [] }
const freeRentStream = new BehaviorSubject<ApiResult<FreeRent[]>>(initialState)
const freeRentEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let freeRentMap = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function sortRent(data: FreeRent[]): FreeRent[] {
  data.sort((a, b) => {
    let A = a["code"], B = b["code"]

    if(A > B) return 1
    else if(A < B) return -1
    else return 0
  });

  return data
}

function clear(): void {
  freeRentStream.next({ id, state: 'ok', value: [] })
  freeRentMap = {}
}

function get(freerentid: number): FreeRent {
  return freeRentMap[freerentid]
}

function refresh(): Promise<any> {
  const currentValue = freeRentStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchFreeRent(propertyid)
}

function fetchFreeRent(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<FreeRent[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  freeRentStream.next({ id, state: 'busy', value: freeRentStream.getValue().value })

  let url = `freerent?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `freerent?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: FreeRent[]) => {
    _.forEach(records, (freerent) => (freeRentMap[freerent.freerentid] = freerent))
    // merge results unique by freerentid
    freeRentStream.next({ id, state: 'ok', value: sortRent(records) })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function create(propertyid: number, defaults: {}): Promise<FreeRent | { message: string }> {
  const currentValue = freeRentStream.getValue().value
  freeRentStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    amttype: defaults['freeRentAmtType'],
    mosnew: null,
    mosrenew: null,
    freerentrevtype: defaults['freeRentRevenue'],
    inputtype: 'basic',
    name: '',
    freerentdetail: {
      state: 'basic'
    }
  }

  return Client.post(`freerent`, payload)
    .then((freerent: FreeRent) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, freerent.freerentid),
        redofunc: () => create(propertyid, defaults)
      })
      freeRentMap[freerent.freerentid] = freerent
      // merge results unique by freerentid
      const currentValue = freeRentStream.getValue().value
      freeRentStream.next({
        id,
        state: 'ok',
        value: _.unionBy([freerent], currentValue, 'freerentid')
      })
      freeRentEventStream.next({ type: 'FREERENT_CREATED', payload: freerent })
      return freerent
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      freeRentStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchFreeRent(propertyid)
      return err
    })
}

function copy(propertyid:number, freerentid:number, code:string) {
  const currentValue = freeRentStream.getValue().value;
  freeRentStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'freerent', recordid: freerentid, _synchronous: true, code: incrementUnique(code, "code", currentValue)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((freerent: FreeRent) => {
      const newRecord = freerent[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.freerentid),
        redofunc: () => copy(propertyid, freerentid, code)
      });
      freeRentMap[newRecord.freerentid] = newRecord
      // merge results unique by freerentid
      const currentValue = freeRentStream.getValue().value
      freeRentStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentValue, 'freerentid').sort((a, b) => alphanum(a.code, b.code))
      })
      freeRentEventStream.next({ type: 'FREERENT_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      freeRentStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchFreeRent(propertyid)
      return err
    })
}

function copies(propertyid: number, freerentid: number, count: number) {
  const currentValue = freeRentStream.getValue().value;
  freeRentStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'freerent', recordid: freerentid, _synchronous: true, unique: true, count};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((freerents: FreeRent[]) => {
      push_undo({
        undofunc: () => _.forEach(freerents, newRecord => deleteOne(propertyid, newRecord.freerentid)),
        redofunc: () => copies(propertyid, freerentid, count)
      });
      _.forEach(freerents, newRecord => freeRentMap[newRecord.freerentid] = newRecord)
      // merge results unique by freerentid
      const currentValue = freeRentStream.getValue().value
      freeRentStream.next({
        id,
        state: 'ok',
        value: _.unionBy(freerents, currentValue, 'freerentid').sort((a, b) => alphanum(a.code, b.code))
      })
      freeRentEventStream.next({ type: 'FREERENT_COPIED', payload: freerents })
      return freerents
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      freeRentStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchFreeRent(propertyid)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  freerentid: number,
): Promise<boolean | { message: string }> {
  const currentValue = freeRentStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, freerentid)
  const refreshFn = () => fetchFreeRent(propertyid)
  if (!(await handleReferences('freerent', freerentid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted freerentid
  const value = _(currentValue)
    .filter((p) => p.freerentid !== freerentid)
    .value()
  value.forEach((p) => (freeRentMap[p.freerentid] = p))

  freeRentStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(freerentid).then(() =>
  Client.del(`freerent?freerentid=eq.${freerentid}`)
    .then((freerent: FreeRent) => {
      delete freeRentMap[freerent.freerentid]
      push_undo({
        undofunc: () => {
          Client.post('freerent', freerent)
          freeRentMap[freerent.freerentid] = freerent
          freeRentStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, freerentid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      freeRentStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchFreeRent(propertyid)
      return err
    })
  )
}

function patch(freerent: {
  freerentid: number
}): Promise<FreeRent | { message: string }> {
  const currentValue = freeRentStream.getValue().value
  const freerentid: number = freerent.freerentid
  const curfreeRentfreeRent = _.find(currentValue, { freerentid })
  const changeset = diff_obj(freerent, curfreeRentfreeRent)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(curfreeRentfreeRent),
    redofunc: () => patch(freerent)
  })

  // optimistic update
  freeRentStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {freerentid}), freerent)
  })

  return _patches_.watch(freerentid, Client.patch(`freerent?freerentid=eq.${freerentid}`, changeset)
    .then((mf: FreeRent[]) => {
      _.forEach(mf, (freerent) => (freeRentMap[freerent.freerentid] = freerent))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      freeRentStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(curfreeRentfreeRent, 'propertyid')
      if (propertyid) fetchFreeRent(propertyid)
      return err
    }))

}

// public module interface //

const freeRentService = {
  stream: () => freeRentStream.asObservable(),
  value: () => freeRentStream.getValue().value,
  eventStream: () => freeRentEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  get,
  fetchFreeRent,
  patch,
  refresh
}

export default freeRentService
